import React from 'react';
import { DollarSign, Inbox } from 'lucide-react';

/**
 * Read-only rate sheet renderer for the customer portal. Mirrors the
 * formatFieldValue helper + show_when conditional logic from the staff-side
 * `frontend/src/modules/crm/profile/ProfileRatesTab.jsx`. Cannot import
 * from the staff app — this is a separate React build — so the helper is
 * duplicated here. Keep the two in sync if the rate-field schema grows.
 */
// Fields that are internal billing mechanics the client never needs to see.
const CLIENT_HIDDEN_FIELD_KEYS = new Set(['bill_starting_period']);

// Client-friendly labels for raw enum/select values (the DB stores codes).
const SELECT_VALUE_LABELS = {
  flat_rate: 'Flat rate',
  msp_managed: 'Per-device managed',
  dollar: 'Dollar ($)',
  percent: 'Percent (%)',
};

function formatFieldValue(value, fieldDef, values) {
  if (value == null || value === '') return '—';
  // Discount is stored as a plain number; its unit comes from the sibling
  // discount_type (dollar vs percent), not the field's own 'currency' type.
  if (fieldDef.key === 'discount' && values && values.discount_type === 'percent') {
    return `${value}%`;
  }
  switch (fieldDef.type) {
    case 'currency':
      return `$${Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    case 'percentage':
      return `${value}%`;
    case 'select':
      return SELECT_VALUE_LABELS[value] || String(value);
    case 'tier_array':
      if (!Array.isArray(value) || value.length === 0) return '—';
      return value
        .map((t) => {
          const cap = t.upto == null ? '∞' : `$${Number(t.upto).toLocaleString()}`;
          return `${t.pct}% up to ${cap}`;
        })
        .join('; ');
    case 'priced_items':
      // Cloud Services & friends: [{label, price}] — one line per item
      if (!Array.isArray(value) || value.length === 0) return '—';
      return (
        <span className="portal-rate-item-list">
          {value.map((it, i) => (
            <span key={i} className="portal-rate-item-row">
              {it.label || 'Item'} — $
              {Number(it.price || 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}/mo
            </span>
          ))}
        </span>
      );
    default:
      // Never leak "[object Object]" to a client — any structured value
      // without a dedicated renderer above shows a dash instead.
      if (typeof value === 'object') return '—';
      return String(value);
  }
}

function shouldShowField(field, values) {
  // Internal billing mechanics — never shown to the client.
  if (CLIENT_HIDDEN_FIELD_KEYS.has(field.key)) return false;
  // Hide "discount type" when there's no discount — avoids noise like
  // "Discount type: Dollar ($)" sitting next to an empty discount.
  if (field.key === 'discount_type') {
    const d = values.discount;
    if (d == null || d === '' || Number(d) === 0) return false;
  }
  if (!field.show_when) return true;
  const sw = field.show_when;
  if (sw.equals !== undefined && values[sw.field] !== sw.equals) return false;
  return true;
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString();
}

export default function RateSheetView({ sheet, entries }) {
  if (!sheet) {
    return (
      <div className="empty-state">
        <Inbox size={48} className="empty-icon" />
        <h3>Rates not yet configured</h3>
        <p>
          Your service rates haven't been set up yet — your account team will
          reach out once they're ready.
        </p>
      </div>
    );
  }

  if (!entries || entries.length === 0) {
    return (
      <div className="empty-state">
        <Inbox size={48} className="empty-icon" />
        <h3>No services on this rate sheet</h3>
        <p>Your account team is still finalizing your service mix.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="portal-rate-sheet-meta">
        <span>Effective {formatDate(sheet.effective_date) || '—'}</span>
        {sheet.notes && (
          <span className="portal-rate-sheet-notes">{sheet.notes}</span>
        )}
      </div>

      <div className="portal-rate-cards">
        {entries.map((entry) => {
          const fields = entry.rate_fields || [];
          const values = entry.rate_values || {};
          const visibleFields = fields.filter((f) => shouldShowField(f, values));

          return (
            <div key={entry.service_type_id} className="portal-rate-card">
              <div className="portal-rate-card-header">
                <DollarSign size={16} />
                <h4>{entry.service_type_name}</h4>
              </div>
              {/* service_description is intentionally NOT rendered here — those
                  descriptions are internal staff notes (e.g. "Should be on every
                  customer rate sheet", "Applied to overage on Limited Managed").
                  The service name is enough for the client. */}
              <div className="portal-rate-card-fields">
                {visibleFields.length === 0 ? (
                  <span className="portal-rate-empty">No values configured</span>
                ) : (
                  visibleFields.map((f) => (
                    <div key={f.key} className="portal-rate-field">
                      <span className="portal-rate-field-label">{f.label}</span>
                      <span className="portal-rate-field-value">
                        {formatFieldValue(values[f.key], f, values)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
